/**
 * Vigilancia de versiones nuevas del service worker.
 *
 * `virtual:pwa-register/react` (usado por `PwaUpdatePrompt`) sólo comprueba si
 * hay versión nueva al cargar la página. En una PWA instalada la pestaña puede
 * quedarse abierta días, así que sin esto el aviso "hay una versión nueva" no
 * aparecería nunca y la persona seguiría con una compilación vieja.
 *
 * Aquí sólo se pide `registration.update()`. Quien decide es la persona: si el
 * navegador encuentra un worker nuevo, éste queda EN ESPERA y el aviso de
 * `PwaUpdatePrompt` sale; nada se activa a la fuerza.
 */

/** Cada cuánto se pregunta por una versión nueva estando la pestaña visible. */
const CHECK_INTERVAL_MS = 60 * 60 * 1000; // 1 hora
/** Tiempo mínimo entre comprobaciones, para no repetirlas al cambiar de app. */
const MIN_GAP_MS = 5 * 60 * 1000; // 5 minutos

let started = false;
let lastCheck = 0;

async function checkForUpdate() {
  if (Date.now() - lastCheck < MIN_GAP_MS) return;
  if (!navigator.onLine) return;
  lastCheck = Date.now();
  try {
    const registration = await navigator.serviceWorker.getRegistration();
    await registration?.update();
  } catch {
    /* sin red o worker recién retirado: se reintenta en el siguiente ciclo */
  }
}

/**
 * Arranca la vigilancia. Es idempotente y no hace nada donde no hay service
 * worker (navegador sin soporte o desarrollo con la PWA apagada).
 */
export function startPwaUpdateWatch() {
  if (started) return;
  if (typeof window === "undefined" || !("serviceWorker" in navigator)) return;
  started = true;

  window.setInterval(() => {
    if (document.visibilityState === "visible") void checkForUpdate();
  }, CHECK_INTERVAL_MS);

  // Volver a la app tras un rato es el momento natural para mirar si hay
  // versión nueva: es cuando la persona está a punto de usarla otra vez.
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") void checkForUpdate();
  });

  window.addEventListener("online", () => void checkForUpdate());
}
